import { createContext, ReactElement, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { constants } from './definitions';
import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import { logger } from '@twiliointernal/e2-commons-shared-libs';
import { systemStatusTypes } from '@twiliointernal/e2-commons-shared-ui';

export const SystemStatusContext =
  createContext<systemStatusTypes.SystemStatusProps>(constants.defaultState);

const fetchSystemStatusFnKey = 'system-status-key';

export function SystemStatusProvider({ children }): ReactElement {
  const [isLoading, toggleIsLoading] = useState<boolean>(true);

  const queryKey = [fetchSystemStatusFnKey];

  async function fetchSystemStatus(): Promise<
    systemStatusTypes.SystemStatus[]
  > {
    const { data, errorCode, message, success } =
      await SupportApi.getSystemStatus();

    toggleIsLoading(false);

    if (!success) {
      const errorMessage = `${constants.LOG_PREFIX}::${errorCode}`;
      logger.error(errorMessage, { message });
      throw Error(errorMessage);
    }

    const systemStatuses = data?.map(({ name, status }) => ({
      name: name.toLowerCase(),
      statusType: constants.systemStatusMap[status],
    }));

    return systemStatuses;
  }

  const { isError, data: systemStatuses } = useQuery({
    retry: false,
    queryKey,
    queryFn: () => fetchSystemStatus(),
  });

  const contextValue = {
    ...constants.defaultState,
    hasError: isError,
    loading: isLoading,
    systemStatuses,
  };

  return (
    <SystemStatusContext.Provider value={contextValue}>
      {children}
    </SystemStatusContext.Provider>
  );
}
